import img1 from "../../assets/Images/Accounting/Trial Balance.png"

export default function TrialBalance() {
    return (
        <section id="trial-balance" className="space-y-16">

            {/* HEADER / OVERVIEW */}
            <section className="bg-white rounded-xl border border-slate-200 p-10">
                <h1 className="text-2xl font-bold text-slate-900 mb-2">
                    5. Trial Balance
                </h1>

                <p className="text-md text-slate-600 max-w-3xl">
                    The Trial Balance module provides a summarized view of all account
                    balances, ensuring that total debits and credits are mathematically
                    balanced.
                </p>

                {/* OVERVIEW */}
                <section className="bg-white border-l border-[#C9A24E] p-4 mt-4">
                    <h2 className="text-lg font-semibold mb-3">
                        Overview
                    </h2>
                    <p className="text-slate-600 text-sm">
                        A trial balance is a key accounting report used to verify the
                        accuracy of ledger postings. It lists all accounts along with their
                        debit or credit balances for a selected period.
                    </p>
                </section>
            </section>

            {/* DATE FILTER */}
            <section className="">
                <div>
                    <h3 className="font-semibold mb-2">
                        Date Range Filter
                    </h3>

                    <p className="text-slate-600 mb-4">
                        Users can generate a trial balance for a specific time period by
                        selecting a start and end date.
                    </p>

                    <ul className="list-disc pl-5 text-slate-600 space-y-1">
                        <li>Start Date (From)</li>
                        <li>End Date (To)</li>
                        <li>Filter applies to all ledger entries</li>
                    </ul>

                    <p className="text-slate-600 mt-3">
                        Clicking the <strong>Filter</strong> button recalculates the trial
                        balance for the selected period.
                    </p>
                </div>

                <div className="rounded-lg p-4 mt-4">
                    <img src={img1} alt="" />
                </div>
            </section>

            {/* EXPORT OPTIONS */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Export & Print Options
                </h3>

                <p className="text-slate-600 mb-4">
                    The Trial Balance can be exported or printed for reporting and audit
                    purposes.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Print trial balance</li>
                    <li>Export to Excel</li>
                    <li>Export to PDF</li>
                </ul>
            </section>

            {/* TRIAL BALANCE TABLE */}
            <section className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Trial Balance Listing
                </h3>

                <p className="text-slate-600 mb-4">
                    The trial balance table displays each account along with its debit or
                    credit balance.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Account name or account code</li>
                    <li>Debit balance (if applicable)</li>
                    <li>Credit balance (if applicable)</li>
                </ul>

                <p className="text-slate-600 mt-3">
                    Each account will appear only on one side — either debit or credit —
                    based on its final balance.
                </p>
            </section>

            {/* TOTALS */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Debit & Credit Totals
                </h3>

                <p className="text-slate-600 mb-4">
                    At the bottom of the report, EventFlow calculates the total debit and
                    total credit amounts.
                </p>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Total Debit = Sum of all debit balances</li>
                    <li>Total Credit = Sum of all credit balances</li>
                    <li>Both totals should be equal</li>
                </ul>

                <p className="text-slate-600 mt-3">
                    If totals do not match, it indicates missing or incorrect accounting
                    entries that must be reviewed.
                </p>
            </section>

            {/* ACCOUNTING SIGNIFICANCE */}
            <section className="bg-white border border-slate-200 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Accounting Significance
                </h3>

                <ul className="list-disc pl-5 text-slate-600 space-y-1">
                    <li>Validates double-entry accounting</li>
                    <li>Detects posting errors</li>
                    <li>Forms the base for financial statements</li>
                    <li>Used before generating Profit & Loss and Balance Sheet</li>
                </ul>
            </section>

            {/* WHY IMPORTANT */}
            <section className="bg-slate-100 rounded-xl p-8">
                <h3 className="font-semibold mb-2">
                    Why Trial Balance Matters
                </h3>

                <p className="text-slate-600 max-w-3xl">
                    The Trial Balance acts as a financial checkpoint. It ensures that all
                    accounting entries are balanced before final reports are prepared,
                    maintaining accuracy, transparency, and trust in financial data.
                </p>
            </section>

        </section>
    );
}
